<div class="dashboard p-3 md:p-5">
  <!-- Header -->
  <div class="text-xs-semibold text-gray-500 dashboard__date">{{ todayLabel }}</div>
  <div class="dashboard-title display-md-semibold text-gray-900 mt-2-5">Dobrodošli nazad, {{ firstName }}!</div>
  <div class="text-md-regular text-gray-600 mt-1">
    Pregled stanja i najvažnijih aktivnosti u sistemu.
  </div>

  <!-- Occupancy section toolbar -->
  <div class="flex flex-column lg:flex-row lg:align-items-end lg:justify-content-between gap-3 mt-5">
    <div class="flex flex-column gap-2">
      <!-- Single metric for now, so a plain title rather than a one-option dropdown. -->
      <span class="text-sm-medium text-gray-600">Popunjenost termina</span>
      @if (loading()) {
        <p-skeleton width="6rem" height="2.25rem"></p-skeleton>
      } @else {
        <div class="flex align-items-center gap-2">
          <span class="display-sm-semibold text-gray-900">{{ formatNumber(data()?.occupancy?.percentage) }}<span class="text-gray-400">&nbsp;%</span></span>
          @if (data()?.occupancy?.trend; as trend) {
            <span class="trend-chip">
              <app-arrow-up-right
                [direction]="trend >= 0 ? 'up-right' : 'down-right'"
                [stroke]="trend >= 0 ? '#17B26A' : '#F04438'"
                width="6px"
                height="6px"
              ></app-arrow-up-right>
              <span class="trend-chip__value">{{ formatNumber(abs(trend)) }}%</span>
            </span>
          }
        </div>
      }
    </div>

    <div class="flex align-items-center gap-2 flex-wrap">
      <p-tabs class="period-tabs" [value]="selectedPeriod()" (valueChange)="selectPeriod($event)">
        <p-tablist>
          @for (p of periods; track p.key) {
            <p-tab [value]="p.key">
              <div class="text-sm-semibold">{{ p.label }}</div>
            </p-tab>
          }
        </p-tablist>
      </p-tabs>
      @if (!editingCustom()) {
        <div
          class="range-chip"
          role="button"
          tabindex="0"
          (click)="selectPeriod('custom')"
          (keydown.enter)="selectPeriod('custom')"
        >
          <app-calendar stroke="#414651" width="20px" height="20px"></app-calendar>
          <span class="range-chip__label text-sm-semibold text-gray-700">{{ customRangeLabel() }}</span>
          @if (selectedPeriod() === 'custom') {
            <button
              type="button"
              class="range-chip__clear"
              aria-label="Obriši period"
              (click)="$event.stopPropagation(); onClearCustom()"
            >
              <app-x-close stroke="#ADB0B8" width="10px" height="10px"></app-x-close>
            </button>
          }
        </div>
      } @else {
        <p-datepicker
          #rangePicker
          class="range-picker"
          selectionMode="range"
          dateFormat="d. M yy"
          [numberOfMonths]="2"
          [readonlyInput]="true"
          [showIcon]="true"
          iconDisplay="input"
          placeholder="Izaberi period"
          [autofocus]="false"
          [showOnFocus]="false"
          [(ngModel)]="customModel"
          (onSelect)="onCustomRange()"
          (click)="onRangePickerClick($event)"
        ></p-datepicker>
      }
    </div>
  </div>

  <!-- Occupancy chart + hours -->
  <div class="flex flex-column lg:flex-row gap-card mt-card">
    <div class="card occupancy-card">
      @if (loading()) {
        <p-skeleton width="100%" height="14rem"></p-skeleton>
      } @else {
        <p-chart
          type="line"
          [data]="appointmentsData()"
          [options]="appointmentsOptions"
          [style]="{ height: '14rem' }"
        ></p-chart>
      }
    </div>

    <div class="card hours-card">
      @if (loading()) {
        <p-skeleton width="100%" height="14rem"></p-skeleton>
      } @else {
        <div class="hours-row">
          <div class="text-sm-regular text-gray-600">Iskorišćeni sati</div>
          <div class="mt-1 display-sm-semibold text-gray-900">{{ formatNumber(data()?.hours?.usedHours) }}<span class="text-md-medium text-gray-400">&nbsp;h</span></div>
          <div class="hours-bar mt-2">
            <div
              class="hours-bar__fill hours-bar__fill--used"
              [style.width.%]="
                (data()?.hours?.totalAvailableHours)
                  ? ((data()?.hours?.usedHours ?? 0) / (data()!.hours!.totalAvailableHours ?? 1)) * 100
                  : 0
              "
            ></div>
          </div>
        </div>

        <div class="hours-row mt-4">
          <div class="text-sm-regular text-gray-600">Slobodni sati</div>
          <div class="mt-1 display-sm-semibold text-gray-900">{{ formatNumber(data()?.hours?.freeHours) }}<span class="text-md-medium text-gray-400">&nbsp;h</span></div>
          <div class="hours-bar mt-2">
            <div
              class="hours-bar__fill hours-bar__fill--free"
              [style.width.%]="
                (data()?.hours?.totalAvailableHours)
                  ? ((data()?.hours?.freeHours ?? 0) / (data()!.hours!.totalAvailableHours ?? 1)) * 100
                  : 0
              "
            ></div>
          </div>
        </div>

        <hr class="divider" />

        <div class="hours-total flex align-items-center justify-content-between">
          <span class="text-sm-semibold text-gray-600">Ukupno dostupno</span>
          <span class="text-sm-semibold text-gray-900">{{ formatNumber(data()?.hours?.totalAvailableHours) }}h</span>
        </div>
      }
    </div>
  </div>

  <!-- Stat cards -->
  <div class="stats-grid mt-card">
    <!-- Revenue -->
    <div class="card stat-card">
      <div class="flex align-items-center gap-3">
        <div class="stat-card__icon">
          <app-wallet-05 stroke="#414651" width="16px" height="16px"></app-wallet-05>
        </div>
        <span class="text-sm-semibold text-gray-600">Ukupan prihod</span>
      </div>
      @if (loading()) {
        <p-skeleton width="8rem" height="2rem" styleClass="mt-3"></p-skeleton>
      } @else {
        <div class="display-sm-semibold text-gray-900 mt-3">
          {{ formatPrice(data()?.revenue?.total) }}<span class="text-gray-400">&nbsp;€</span>
        </div>
        <hr class="divider" />
        @if (data()?.revenue?.trend; as trend) {
          <span class="trend" [class.trend--up]="trend >= 0" [class.trend--down]="trend < 0">
            <app-arrow
              [direction]="trend >= 0 ? 'up' : 'down'"
              [stroke]="trend >= 0 ? '#17B26A' : '#F04438'"
              width="12px"
              height="12px"
            ></app-arrow>
            {{ formatNumber(abs(trend)) }}%
          </span>
        }
      }
    </div>

    <!-- Sessions -->
    <div class="card stat-card">
      <div class="flex align-items-center gap-3">
        <div class="stat-card__icon">
          <app-calendar-check-02 stroke="#414651" width="16px" height="16px"></app-calendar-check-02>
        </div>
        <span class="text-sm-semibold text-gray-600">Ukupno sesija</span>
      </div>
      @if (loading()) {
        <p-skeleton width="8rem" height="2rem" styleClass="mt-3"></p-skeleton>
      } @else {
        <div class="display-sm-semibold text-gray-900 mt-3">
          {{ data()?.sessions?.total ?? 0 }}
        </div>
        <hr class="divider" />
        <div class="text-sm-regular text-gray-500">
          <strong>{{ data()?.sessions?.legalEntityCount ?? 0 }}</strong> pravnih,
          <strong>{{ data()?.sessions?.individualCount ?? 0 }}</strong> fizičkih lica
        </div>
      }
    </div>

    <!-- Cancellation -->
    <div class="card stat-card">
      <div class="flex align-items-center gap-3">
        <div class="stat-card__icon">
          <app-slash-circle-01 stroke="#414651" width="18px" height="18px"></app-slash-circle-01>
        </div>
        <span class="text-sm-semibold text-gray-600">Stopa otkazivanja</span>
      </div>
      @if (loading()) {
        <p-skeleton width="8rem" height="2rem" styleClass="mt-3"></p-skeleton>
      } @else {
        <div class="display-sm-semibold text-gray-900 mt-3">
          {{ formatNumber(data()?.cancellation?.rate) }}<span class="text-gray-400">&nbsp;%</span>
        </div>
        <hr class="divider" />
        @if (data()?.cancellation?.trend; as trend) {
          <span class="trend" [class.trend--up]="trend >= 0" [class.trend--down]="trend < 0">
            <app-arrow
              [direction]="trend >= 0 ? 'up' : 'down'"
              [stroke]="trend >= 0 ? '#17B26A' : '#F04438'"
              width="12px"
              height="12px"
            ></app-arrow>
            {{ formatNumber(abs(trend)) }}%
          </span>
        }
      }
    </div>
  </div>

  <!-- Revenue charts -->
  <div class="flex flex-column lg:flex-row gap-card mt-card">
    <div class="card revenue-chart-card">
      <div class="text-lg-semibold text-gray-900">Prihod po tipu paketa</div>
      <hr class="divider" />
      @if (loading()) {
        <p-skeleton width="100%" height="16rem"></p-skeleton>
      } @else {
        <p-chart
          class="block"
          type="bar"
          [data]="revenueByPackageData()"
          [options]="revenueByPackageOptions"
          [style]="{ height: '16rem' }"
        ></p-chart>
      }
    </div>

    <div class="card revenue-type-card">
      <div class="text-lg-semibold text-gray-900">Prihod po tipu klijenta</div>
      <hr class="divider" />
      @if (loading()) {
        <p-skeleton shape="circle" size="11rem"></p-skeleton>
      } @else {
        <p-chart
          class="block"
          type="doughnut"
          [data]="revenueByClientTypeData()"
          [options]="revenueByClientTypeOptions"
          [style]="{ height: '16rem' }"
        ></p-chart>
        <div class="flex flex-column gap-2 mt-4">
          @for (row of revenueByClientTypeLegend(); track row.label) {
            <div class="flex align-items-center justify-content-between gap-3">
              <span class="flex align-items-center gap-2 text-sm-medium text-gray-600">
                <span class="legend-dot" [style.background]="row.color"></span>
                {{ row.label }}
              </span>
              <span class="text-sm-semibold text-gray-900 white-space-nowrap">
                {{ formatPrice(row.value) }}€
                <span class="text-gray-500 text-sm-medium ml-1">{{ formatNumber(row.percentage) }}%</span>
              </span>
            </div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Active requests -->
  <div class="card mt-card">
    <div class="text-lg-semibold text-gray-900">Aktivni zahtevi</div>
    <hr class="divider" />

    @if (loading()) {
      <div class="flex flex-column gap-2">
        @for (r of [1, 2]; track r) {
          <p-skeleton width="100%" height="4rem"></p-skeleton>
        }
      </div>
    } @else {
      @for (req of data()?.activeRequests ?? []; track req.status?.value) {
        <div class="request-row" [class.mt-3]="!$first">
          <div class="flex align-items-center gap-3 request-main">
            <span class="request-badge" [class]="requestBadgeClass(req.status)">
              {{ req.count ?? 0 }}
            </span>
            <div class="flex flex-column gap-1 request-info">
              <span class="text-sm-semibold text-gray-900">
                {{ requestLabel(req.status, req.count) }}
              </span>
              @if (req.lastRequestDateTime) {
                <span class="flex flex-wrap align-items-center text-sm-medium request-meta">
                  <app-calendar stroke="#ADB0B8" width="12px" height="12px"></app-calendar>
                  <span class="text-gray-500">Poslednji:</span>
                  <span class="text-gray-600 request-date">{{ getDateFormatedSrWithTime(req.lastRequestDateTime) }}</span>
                </span>
              }
            </div>
          </div>
          <button type="button" class="request-link text-sm-semibold" (click)="goToRequests()">
            Vidi zahteve
            <app-chevron-right stroke="#2970FF" width="12px" height="12px"></app-chevron-right>
          </button>
        </div>
      } @empty {
        <div class="text-sm-regular text-gray-500">Trenutno nema aktivnih zahteva.</div>
      }
    }
  </div>
</div>
